import { useContext, useLayoutEffect, useMemo, useState, type FC } from "react"
import { useTranslation } from "react-i18next"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import type { Powerline } from "types/powerlines.types"
import type { GeoJSONLayersData } from "types/geolocation.types"

import DashboardContext from "helpers/contexts/DashboardContext"
import {
  limitingSpanFrequencyView,
  SPAN_LAYOUT_MAP,
} from "helpers/config/dashboards/powerline/defs"
import useLimitingSpanFrequency from "helpers/hooks/powerlines/useLimitingSpanFrequency"
import useSpanFrequencyColors from "helpers/hooks/useSpanFrequencyColors"
import { getGeoBounds, getMapCenterFromGeo, hasPowerlineRoute } from "helpers/utils/map"
import { formatTimestamp } from "helpers/utils/dateUtils"
import { formatPlotTitleTooltip } from "helpers/utils/plots"
import { PlotContainer } from "widgets/styled/containers"
import PlotHelpTooltip from "widgets/plots/PlotHelpTooltip"
import GeoMap from "widgets/plots/geo/Map"
import GeoJSONLayers from "widgets/plots/geo/GeoJSONLayers"
import LimitingSpansHeatmap from "widgets/plots/LimitingSpansHeatmap"
import { getCurrentLimiterMethods } from "widgets/plots/LimitingSpansHeatmap/helpers"
import RibbonSkeleton from "widgets/plots/LimitingSpansHeatmap/RibbonSkeleton"
import RecenterControl from "widgets/plots/geo/RecenterControl"
import SpanSummaryCard from "widgets/plots/powerlines/SpanSummaryCard"
import PlotRetryChip from "widgets/plots/PlotRetryChip"

import {
  CARD_ROWS,
  FLY_TO_DURATION_S,
  getLastLimiterBySpanId,
  getPeakSpanMarkers,
  getRibbonBlocks,
  getRibbonMarkers,
  getSpanDataById,
  getSpanGeoById,
  getSpanMapState,
  getSpanTotals,
  HEADER_GAP,
  HEADER_HEIGHT,
  MAP_LAYERS_VISIBILITY,
  MIN_MAP_HEIGHT,
  MOBILE_RIBBON_WINDOW,
  RIBBON_GAP,
} from "./helpers"
import useSpanSelection from "./useSpanSelection"

interface SpanLayoutMapProps {
  powerline: Powerline
  // Undefined until the powerline's GeoJSON lands. The card owns that wait.
  geoData?: GeoJSONLayersData
  // The powerline or its GeoJSON is still in flight.
  isGeoLoading: boolean
}

const SpanLayoutMap: FC<SpanLayoutMapProps> = ({ powerline, geoData, isGeoLoading }) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"))
  const { colorFor } = useSpanFrequencyColors()

  const {
    spanData,
    maxPct,
    highlights,
    range,
    isLoading,
    isPending,
    isSkipped,
    error,
    hasPartialError: hasPartialFrequencyError,
    methodErrors,
    hasCounts,
    refetch: refetchFrequency,
  } = useLimitingSpanFrequency({
    powerlineId: powerline.id,
    orderedSpanIds: powerline.span_ids,
  })

  const state = getSpanMapState({
    isGeoLoading,
    hasRoute: hasPowerlineRoute(geoData),
    isPending,
    hasCounts,
    methodErrors,
    isSkipped,
  })
  const isFrequency = state === "frequency"

  const hasFailed = Boolean(error)
  const hasPartialError = hasFailed && hasPartialFrequencyError

  const center = useMemo(() => getMapCenterFromGeo(geoData), [geoData])

  const spanDataById = useMemo(() => getSpanDataById(spanData), [spanData])

  const peakSpanMarkers = useMemo(
    () =>
      isFrequency
        ? getPeakSpanMarkers({ highlights, spanDataById, maxPct, colorFor, t })
        : [],
    [isFrequency, highlights, spanDataById, maxPct, colorFor, t],
  )

  const lastLimiterBySpanId = useMemo(
    () => getLastLimiterBySpanId(highlights),
    [highlights],
  )

  const ribbonMarkers = useMemo(() => getRibbonMarkers(highlights), [highlights])

  // The frequency paragraphs come in with the half of the title that
  // introduces them.
  const helpTooltipContent = useMemo(
    () =>
      formatPlotTitleTooltip(
        [
          "plots_descriptions.SPAN_LAYOUT",
          ...(isFrequency ? (limitingSpanFrequencyView.helpTooltip ?? []) : []),
        ],
        t,
        i18n.resolvedLanguage,
      ),
    [isFrequency, t, i18n.resolvedLanguage],
  )

  // The window the counts cover, written out for the readouts to carry.
  const dateRangeLabel = useMemo(
    () =>
      isFrequency && range
        ? String(
            t("spans.ampacity.window.dateRange", {
              from: formatTimestamp(range.fromDate),
              to: formatTimestamp(range.toDate),
            }),
          )
        : undefined,
    [isFrequency, range, t],
  )

  const spanGeoById = useMemo(() => getSpanGeoById(geoData), [geoData])

  const bounds = useMemo(() => getGeoBounds(geoData), [geoData])

  const totals = useMemo(() => getSpanTotals(spanData), [spanData])

  const {
    hoveredSpanId,
    selectedSpanId,
    setSelectedSpanId,
    isFlying,
    handleHoverSpan,
    handleSelectSpan,
    handleMapReady,
  } = useSpanSelection({
    powerlineId: powerline.id,
    isNarrow,
    state,
    spanData,
    highlights,
    spanGeoById,
  })

  // Hover on a pointer device, the pinned selection on touch.
  const readoutSpanId = isNarrow ? selectedSpanId : hoveredSpanId
  const readoutDatum = readoutSpanId != null ? spanDataById.get(readoutSpanId) : undefined
  const readoutMeta = readoutSpanId != null ? spanGeoById.get(readoutSpanId) : undefined

  const ribbonModeProps = useMemo(
    () =>
      isFrequency
        ? ({
            mode: "frequency",
            maxPct,
            markersByKey: ribbonMarkers,
            laneErrors: methodErrors,
          } as const)
        : ({ mode: "neutral" } as const),
    [isFrequency, maxPct, ribbonMarkers, methodErrors],
  )

  // Methods whose last reported limiting span is the one being read out.
  const readoutLastLimiterMethods = useMemo(
    () => getCurrentLimiterMethods(readoutSpanId, ribbonMarkers),
    [readoutSpanId, ribbonMarkers],
  )

  // The readout's mode and everything that comes with it, so a neutral card
  // can't be handed a figure it wouldn't print. Both variants take the same
  // object; the overlay renders the subset it has room for.
  const summaryModeProps = useMemo(
    () =>
      isFrequency
        ? ({
            mode: "frequency",
            maxPct,
            totals,
            methodErrors,
            lastLimiterMethods: readoutLastLimiterMethods,
            dateRangeLabel,
          } as const)
        : ({ mode: "neutral" } as const),
    [
      isFrequency,
      maxPct,
      totals,
      methodErrors,
      readoutLastLimiterMethods,
      dateRangeLabel,
    ],
  )

  // What the ribbon holds in each shape. The skeleton takes one of these, so
  // it reserves what the real thing will need.
  const blocks = useMemo(
    () => getRibbonBlocks(isNarrow, spanData.length),
    [isNarrow, spanData.length],
  )

  // The shape the card last settled on. A pending layer keeps it, so a new date
  // range swaps in a skeleton at the same size instead of resizing twice.
  // Neutral is the opening guess: until something answers, that's what it is.
  const [settledShape, setSettledShape] = useState<"frequency" | "neutral">("neutral")
  if ((state === "frequency" || state === "neutral") && state !== settledShape) {
    setSettledShape(state)
  }
  const shape = state === "layerLoading" ? settledShape : state

  const { plotLayout, dispatchPlotLayout } = useContext(DashboardContext) ?? {}
  // Above the early return, so the cell is sized even when nothing is drawn.
  // Before paint, not after: the taller strip renders in the same commit as the
  // shape change, so a `useEffect` leaves one frame where the map has shrunk to
  // fit it inside a cell that hasn't grown yet.
  useLayoutEffect(() => {
    // A resize mid-`flyTo` reaches leaflet as an `invalidateSize` and derails
    // the pan. The effect re-runs when the fly settles.
    if (isFlying) return
    const rows = CARD_ROWS[shape]
    dispatchPlotLayout?.({
      type: "UPDATE_CELL_HEIGHT",
      payload: { id: SPAN_LAYOUT_MAP, h: isNarrow ? rows.narrow : rows.wide },
    })
  }, [shape, isNarrow, isFlying, dispatchPlotLayout])

  const containerSx = {
    display: "flex",
    flexDirection: "column" as const,
    height: "100%",
    borderColor: theme.palette.neutral[400],
  }

  // No geometry, nothing to draw. The cell is already collapsed above.
  if (state === "collapsed" || !geoData) return null

  // The height lands a paint after this render, so on the first frame a
  // full-size card would spill out of a still-collapsed cell. Only on `0`:
  // blanking over a small mismatch is worse than the mismatch.
  if (plotLayout?.[SPAN_LAYOUT_MAP]?.h === 0) return null

  return (
    <PlotContainer
      maxWidth="xl"
      type="line"
      sx={{
        ...containerSx,
        borderColor: hasFailed
          ? hasPartialError
            ? theme.palette.warning.main
            : theme.palette.error.main
          : theme.palette.neutral[400],
      }}
    >
      <Box style={{ display: "flex", justifyContent: "space-between" }}>
        {/* Titled in every state: the card is the line's spans, on the map and
            in order along the route. The title names the ampacity layer when
            there is one, in the same slot, so nothing shifts. */}
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ height: HEADER_HEIGHT, pb: `${HEADER_GAP}px` }}
        >
          <Typography variant="h5">
            {t(
              isFrequency
                ? "plot_titles.SPAN_LAYOUT_LIMITING_SPANS"
                : "plot_titles.SPAN_LAYOUT",
            )}
          </Typography>
          {/* This card renders its views itself rather than going through
              `Plot`, so the help text `Plot` would show is hung here. */}
          <PlotHelpTooltip content={helpTooltipContent} />
          {isLoading && <CircularProgress size={20} />}
          {hasFailed && (
            <PlotRetryChip
              hasPartialError={hasPartialError}
              isRetrying={isLoading}
              onClick={refetchFrequency}
            />
          )}
        </Stack>
        <DragIndicatorIcon className="dragIcon" data-testid="drag" />
      </Box>

      {/* The map takes whatever height the rest of the card leaves. Everything
          below is fixed, so this is the one piece that can absorb the slack,
          and nothing needs re-tuning when a row is added under it. */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: MIN_MAP_HEIGHT,
        }}
      >
        <GeoMap
          key={`limiting-span-map-${powerline.id}`}
          center={center}
          mapHeight="100%"
          initialZoomLevel={11}
          // Picking a span in the ribbon already drives the map, so don't make
          // the veil ask for a click as well.
          activated={selectedSpanId != null}
          showZoomControl={!isNarrow}
          onMapReady={handleMapReady}
        >
          <GeoJSONLayers
            data={geoData}
            layersVisibility={MAP_LAYERS_VISIBILITY}
            compactDevices
            spans={{
              neutralRoute: true,
              lastLimiterBySpanId: isFrequency ? lastLimiterBySpanId : undefined,
              peakSpanMarkers,
              hoveredSpanId,
              selectedSpanId,
              onHoverSpan: handleHoverSpan,
              onSelectSpan: handleSelectSpan,
              // On touch the selection is all we have, so closing a popup must
              // not clear it. Neutral too: the readout and the strip follow it.
              onSelectedSpanChange: isNarrow ? undefined : setSelectedSpanId,
              popupOpenDelay: FLY_TO_DURATION_S * 1000,
              // On a phone the card below already says this, so an automatic
              // popup would just cover the map. Tapping still opens one.
              autoOpenSpanPopups: !isNarrow,
            }}
          />
          <RecenterControl
            bounds={bounds}
            title={String(t("spans.ampacity.controls.recenter"))}
          />
        </GeoMap>

        {/* Top-right, just left of the layers control. Wide screens only: on a
            phone it would land on the controls, and the block under the map
            says the same thing with room to say it. */}
        {!isNarrow && readoutDatum && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              right: 56,
              zIndex: 500,
              pointerEvents: "none",
            }}
          >
            <SpanSummaryCard
              datum={readoutDatum}
              meta={readoutMeta}
              variant="overlay"
              {...summaryModeProps}
            />
          </Box>
        )}
      </Box>

      {/* No hover and no room for an overlay, so the readout becomes its own
          block. Not while the layer loads: with nothing selected it would say
          "no limiting-span data" over queries that are still running. */}
      {isNarrow && state !== "layerLoading" && (
        <Box sx={{ mt: 1, flexShrink: 0 }}>
          <SpanSummaryCard
            datum={readoutDatum}
            meta={readoutMeta}
            variant="block"
            {...summaryModeProps}
          />
        </Box>
      )}

      {/* Stands in for the strip at its own size. It replaces it rather than
          sitting under it: a strip that turns into a different strip mid-load
          reads as two answers to one question. */}
      {state === "layerLoading" ? (
        <Box sx={{ flexShrink: 0, mt: `${RIBBON_GAP}px` }}>
          <RibbonSkeleton {...blocks[settledShape]} />
        </Box>
      ) : (
        /* Otherwise always drawn: the spans come from the powerline, not the
           telemetry, so the strip has something to show either way. A failed
           method becomes a hatched lane, not an error screen — the retry chip
           is how you recover. */
        <Box sx={{ flexShrink: 0, mt: `${RIBBON_GAP}px` }}>
          <LimitingSpansHeatmap
            view={limitingSpanFrequencyView}
            spanData={spanData}
            {...ribbonModeProps}
            hoveredSpanId={hoveredSpanId}
            selectedSpanId={selectedSpanId}
            onHoverSpan={handleHoverSpan}
            onSelectSpan={handleSelectSpan}
            windowConfig={isNarrow ? MOBILE_RIBBON_WINDOW : undefined}
            showSummaryRow={!isNarrow}
            dateRangeLabel={dateRangeLabel}
          />
        </Box>
      )}
    </PlotContainer>
  )
}

export default SpanLayoutMap
